import { makeAutoObservable, runInAction } from 'mobx';
import {
    RosterDosPreset,
    rosterClaimsInRange,
    rosterDosPresetRange,
    rosterEffectiveSelection,
    rosterIsClaimLocked,
    rosterSelectableClaims,
    rosterToggleAllInRange,
    rosterToggleClaim,
} from '../../services/roster/roster.claims';
import { rosterComingSoon } from '../../services/roster/roster.types';
import type {
    RosterApplyResult,
    RosterClaim,
    RosterDataSource,
    RosterDosRange,
    RosterRecordRef,
} from '../../services/roster/roster.types';

/**
 * "Apply changes to existing claims?" — the two steps after an existing provider or facility is
 * saved (FE-109).
 *
 * Step `ask`: how many claims reference the record, and whether to touch them at all. Step
 * `select`: narrow by date of service, pick claims, choose whether to retransmit, apply.
 *
 * The rules live in `roster.claims.ts` and are only composed here: a PAID claim is locked and can
 * never be in the applied selection, and narrowing the range drops what fell out of it — the
 * "Update N claims" count is always the selection that would actually be sent.
 *
 * **A failed lookup is not "no claims" (NS-3).** `status: 'error'` says the claims are unknown; the
 * dialog must not offer "0 claims reference this provider" on the strength of a failed request.
 */
export class RosterClaimsFlow {
    public readonly ref: RosterRecordRef;
    public readonly name: string;

    public status: 'loading' | 'ready' | 'error' = 'loading';
    public error: string | undefined = undefined;
    public claims: RosterClaim[] = [];
    public step: 'ask' | 'select' = 'ask';
    public range: RosterDosRange = {};
    public selectedIds: string[] = [];
    /** Retransmit as corrected claims. On by default, as in the design. */
    public retransmit = true;
    public applying = false;

    private readonly dataSource: RosterDataSource;
    private readonly now: () => Date;

    constructor(dataSource: RosterDataSource, ref: RosterRecordRef, name: string, now: () => Date = () => new Date()) {
        this.dataSource = dataSource;
        this.ref = ref;
        this.name = name;
        this.now = now;
        makeAutoObservable<RosterClaimsFlow, 'dataSource' | 'now'>(
            this,
            { ref: false, name: false, dataSource: false, now: false },
            { autoBind: true },
        );
    }

    public get noun(): 'provider' | 'facility' {
        return this.ref.tab === 'providers' ? 'provider' : 'facility';
    }

    public get inRange(): RosterClaim[] {
        return rosterClaimsInRange(this.claims, this.range);
    }

    /** What "Update N claims" would send: picked, in range, and not locked. */
    public get selection(): string[] {
        return rosterEffectiveSelection(this.selectedIds, this.claims, this.range);
    }

    public get allSelected(): boolean {
        const selectable = rosterSelectableClaims(this.claims, this.range);
        return selectable.length > 0 && this.selection.length === selectable.length;
    }

    public get lockedInRange(): number {
        return this.inRange.filter(rosterIsClaimLocked).length;
    }

    public isSelected(claim: RosterClaim): boolean {
        return this.selection.includes(claim.id);
    }

    public async load(): Promise<void> {
        this.status = 'loading';
        this.error = undefined;
        try {
            const claims = await this.dataSource.listClaimsFor(this.ref);
            runInAction(() => {
                this.claims = claims;
                this.status = 'ready';
            });
        } catch (error) {
            runInAction(() => {
                this.claims = [];
                this.status = 'error';
                this.error = error instanceof Error && error.message ? error.message : 'Request failed';
            });
        }
    }

    public chooseClaims(): void {
        this.step = 'select';
    }

    /** Either bound may be cleared; `YYYY-MM-DD`. The selection is pruned to what is still in range. */
    public setRange(range: RosterDosRange): void {
        this.range = range;
        this.selectedIds = rosterEffectiveSelection(this.selectedIds, this.claims, range);
    }

    public applyPreset(preset: RosterDosPreset): void {
        this.setRange(rosterDosPresetRange(preset, this.now()));
    }

    public toggle(claim: RosterClaim): void {
        this.selectedIds = rosterToggleClaim(this.selection, claim);
    }

    public toggleAll(): void {
        this.selectedIds = rosterToggleAllInRange(this.selection, this.claims, this.range);
    }

    public setRetransmit(retransmit: boolean): void {
        this.retransmit = retransmit;
    }

    /**
     * Whether "Update N claims" can run. Re-applying has no endpoint yet on either API: the step
     * still lists the real claims, and the button waits disabled under a "Coming soon" hint (NS-4).
     */
    public get applyAvailable(): boolean {
        return !rosterComingSoon(this.dataSource.capabilities, 'applyToClaims');
    }

    /** Throws on failure, leaving the step and selection as they were so the user can retry. */
    public async apply(): Promise<RosterApplyResult> {
        if (!this.applyAvailable) {
            throw new Error('Updating existing claims is not available yet.');
        }
        const ids = this.selection;
        if (ids.length === 0) {
            throw new Error('Select at least one claim.');
        }
        this.applying = true;
        try {
            return await this.dataSource.applyToClaims(this.ref, ids, { retransmit: this.retransmit });
        } finally {
            runInAction(() => {
                this.applying = false;
            });
        }
    }
}
